import React, { useEffect, useState } from 'react'
import Topbar from '../../Components/Topbar'
import LeftbarUser from '../../Components/LeftbarUser'
import BottomNavUser from '../../Components/BottomNavUser'
import { useNavigate } from 'react-router-dom'
import { getFirestore, collection, getDocs } from 'firebase/firestore'
import { app } from '../../firebase'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faInstagram, faTiktok, faYoutube, faTwitter } from '@fortawesome/free-brands-svg-icons'
import defProfile from '../../Images/def.png'
import wavyImage from '../../Images/wavy.png'
import { Link } from 'react-router-dom'  // add this at the top


function RunAds() {
  const navigate = useNavigate()
  const db = getFirestore(app)

  const [step, setStep] = useState(1)
  const [platform, setPlatform] = useState('')
  const [tier, setTier] = useState('')
  const [category, setCategory] = useState('')
  const [adType, setAdType] = useState('')
  const [budget, setBudget] = useState('')
  const [loading, setLoading] = useState(false)

  const [allInfluencers, setAllInfluencers] = useState([])
  const [platformInfluencers, setPlatformInfluencers] = useState([])
  const [filteredInfluencers, setFilteredInfluencers] = useState([])
  const [categoryFilteredInfluencers, setCategoryFilteredInfluencers] = useState([])
  const [budgetFiltered, setBudgetFiltered] = useState([])

  // ====== SEO & Meta Tags ======
  useEffect(() => {
    // Page title
    document.title = "Zindua App | Run Ads";

    // Meta description
    let desc = document.querySelector("meta[name='description']");
    if (!desc) {
      desc = document.createElement("meta");
      desc.setAttribute("name", "description");
      document.head.appendChild(desc);
    }
    desc.setAttribute("content", "Run ad campaigns on Zindua App to connect with verified influencers across Instagram, TikTok, YouTube, and Twitter.");

    // Open Graph tags
    const ogTags = [
      { property: "og:title", content: "Zindua App | Run Ads" },
      { property: "og:description", content: "Run ad campaigns on Zindua App to connect with verified influencers across Instagram, TikTok, YouTube, and Twitter." },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://zindua.app/user/run-ads" },
      { property: "og:image", content: "https://zindua.app/logo192.png" },
    ];

    ogTags.forEach(({ property, content }) => {
      let tag = document.querySelector(`meta[property='${property}']`);
      if (!tag) {
        tag = document.createElement("meta");
        tag.setAttribute("property", property);
        document.head.appendChild(tag);
      }
      tag.setAttribute("content", content);
    });

    // JSON-LD structured data
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "WebPage",
      name: "Run Ads on Zindua App",
      url: "https://zindua.app/user/run-ads",
      description: "Run ad campaigns on Zindua App to connect with verified influencers across Instagram, TikTok, YouTube, and Twitter.",
      publisher: {
        "@type": "Organization",
        name: "Zindua",
        logo: "https://zindua.app/logo192.png",
        sameAs: [
          "https://www.instagram.com/zindua.app/",
          "https://www.linkedin.com/company/zindua-app/",
          "https://www.tiktok.com/@zindua.app"
        ]
      }
    });
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);


  const tiers = [
    { label: 'Nano (1k – 5k followers)', min: 1000, max: 5000 },
    { label: 'Micro (5,001 – 10k followers)', min: 5001, max: 10000 },
    { label: 'Rising (10,001 – 50k followers)', min: 10001, max: 50000 },
    { label: 'Mid-tier (50,001 – 100k followers)', min: 50001, max: 100000 },
    { label: 'Macro (100,001 – 500k followers)', min: 100001, max: 500000 },
    { label: 'Mega (500,001 – 1M followers)', min: 500001, max: 1000000 },
    { label: 'Celebrity (1M+ followers)', min: 1000001, max: Infinity }
  ]

  const categories = [
    'Agriculture',
    'alcohol',
    'Automotive',
    'Beauty and Sanitation',
    'Chemical',
    'crypto',
    'Construction',
    'Education',
    'Electronics Manufacturing',
    'Electronics Retail',
    'Entertainment',
    'Events',
    'Fashion and Beauty',
    'Finance',
    'Financial Services',
    'Food and Beverage',
    'Forestry, Wood, Pulp and Paper',
    'forex',
    'General Retail and Commerce',
    'Government Agency',
    'gambling',
    'Health and Fitness',
    'Health Care and Social Assistance',
    'Hotels, Tourism, Food and Catering',
    'Information Technology',
    'Insurance',
    'Manufacturing',
    'Mechanical and Electrical Engineering',
    'Media',
    'Media and News',
    'Mining',
    'Music Industry',
    'NGO or Community',
    'Oil and Gas',
    'Online Trade',
    'political',
    'Postal and Telecommunications',
    'Professional Services',
    'Professional, Scientific and Technical Services',
    'Public Service',
    'religious',
    'Real Estate',
    'Retail',
    'socialIssues',
    'Technology',
    'trading',
    'Textiles, Clothing, Leather and Footwear',
    'Transport and Warehousing',
    'Travel',
    'Utilities',
    'Wholesale Trade',
    'other'
  ]

  // Fetch influencers
  useEffect(() => {
    const fetchInfluencers = async () => {
      setLoading(true)
      const snap = await getDocs(collection(db, 'users'))
      const influencers = snap.docs
        .map(d => {
          const data = d.data()
          return {
            id: d.id,
            ...data,
            unacceptedCategories: data.adsNotAccepted || {}
          }
        })
        .filter(u => u.userType === 'An Influencer' && u.accountIsVerified)
      setAllInfluencers(influencers)
      setLoading(false)
    }
    fetchInfluencers()
  }, [])

  // Platform filtering
  useEffect(() => {
    if (!platform || platform === 'all') {
      setPlatformInfluencers(allInfluencers)
      return
    }
    const filtered = allInfluencers.filter(u =>
      u.socials?.[platform] === true || u.socials?.[platform] === 'selected'
    )
    setPlatformInfluencers(filtered)
  }, [platform, allInfluencers])

  // Tier filtering
  useEffect(() => {
    const tierFiltered = (() => {
      if (!tier) return platformInfluencers
      const tierObj = tiers.find(t => t.label === tier)
      if (!tierObj) return []
      return platformInfluencers.filter(u => {
        let followers = 0
        if (platform === 'all') {
          ['instagram', 'tiktok', 'twitter'].forEach(pl => {
            followers += u.socialStats?.[pl]?.followers || 0
          })

          followers += u.socialStats?.youtube?.subscribers || 0
        } else {
          followers =
            platform === 'youtube'
              ? u.socialStats?.youtube?.subscribers || 0
              : u.socialStats?.[platform]?.followers || 0

        }
        return followers >= tierObj.min && followers <= tierObj.max
      })
    })()
    setFilteredInfluencers(tierFiltered)
  }, [tier, platform, platformInfluencers])

  // Category filtering: exclude influencers who do not accept the selected category
  useEffect(() => {
    if (!category) {
      setCategoryFilteredInfluencers(filteredInfluencers)
      return
    }
    const filteredByCategory = filteredInfluencers.filter(u => !u.unacceptedCategories[category])
    setCategoryFilteredInfluencers(filteredByCategory)
  }, [category, filteredInfluencers])

  useEffect(() => {
    if (!budget || !adType) {
      setBudgetFiltered(categoryFilteredInfluencers)
      return
    }

    const budgetNum = Number(budget)

    const influencersWithPrice = categoryFilteredInfluencers
      .map(u => {
        if (!platform || !u.servicePricing?.[platform]) return null
        const platPricing = u.servicePricing[platform]
        const price =
          adType === 'cgc'
            ? platPricing.creatorGeneratedContent?.price || 0
            : platPricing.directBrandContent?.post || 0
        if (price <= 0) return null
        return { ...u, price }
      })
      .filter(Boolean)
      .sort(() => Math.random() - 0.5)

    let total = 0
    const selected = []
    for (const inf of influencersWithPrice) {
      if (total + inf.price <= budgetNum) {
        selected.push(inf)
        total += inf.price
      }
    }

    setBudgetFiltered(selected)
  }, [budget, categoryFilteredInfluencers, adType, platform])


  const platformIcons = { instagram: faInstagram, tiktok: faTiktok, youtube: faYoutube, twitter: faTwitter }

  return (
    <div className="mainDashboard">
      <div className="desktopOnly">
        <LeftbarUser />
      </div>
      <div className="mainContent">
        <Topbar />
        <section className="runAds container mt-3">

          {/* STEP 1 */}
          {step === 1 && (
            <>
              <div className='runmyads'>
                <img src={wavyImage} alt="" className='wavyImage' />
                <h4>Run Campaigns</h4>
                <p>Choose how you want to run your ads</p>
                <div className="d-flex gap-3 mt-3">
                  <button className="runmyadsBtn1" onClick={() => navigate('/user/influencers')}>Single Creator</button>
                  <button className="runmyadsBtn2" onClick={() => setStep(2)}>Campaign (Multiple Creators)</button>
                </div>
              </div>
            </>
          )}

          {/* STEP 2: Platform */}
          {step === 2 && (
            <>
              <h5>Select Platform</h5>
              <select className="form-select mb-3" value={platform} onChange={e => setPlatform(e.target.value)}>
                <option value="">Choose platform</option>
                <option value="instagram">Instagram</option>
                <option value="tiktok">TikTok</option>
                <option value="twitter">Twitter</option>
                <option value="youtube">YouTube</option>
              </select>

              <div className="mb-3">
                <button className="btn btn-secondary me-2" onClick={() => setStep(1)}>Previous</button>
                <button className="btn btn-primary" onClick={() => setStep(3)} disabled={!platform}>Next</button>
              </div>

              <InfluencerList
                influencers={categoryFilteredInfluencers}
                platform={platform}
                adType={adType}
                platformIcons={platformIcons}
              />


            </>
          )}

          {/* STEP 3: Tier */}
          {step === 3 && (
            <>
              <h5>Filter by Influencer Tier</h5>
              <select className="form-select mb-3" value={tier} onChange={e => setTier(e.target.value)}>
                <option value="">Choose Tier</option>
                {tiers.map(t => <option key={t.label} value={t.label}>{t.label}</option>)}
              </select>
              <div className="mb-3">
                <button className="btn btn-secondary me-2" onClick={() => setStep(2)}>Previous</button>
                <button className="btn btn-primary" onClick={() => setStep(4)} disabled={filteredInfluencers.length === 0}>Next</button>
              </div>
              <InfluencerList
                influencers={categoryFilteredInfluencers}
                platform={platform}
                adType={adType}
                platformIcons={platformIcons}
              />

            </>
          )}

          {/* STEP 4: Category */}
          {step === 4 && (
            <>
              <h5>Select Business/Service Category</h5>
              <select className="form-select mb-3" value={category} onChange={e => setCategory(e.target.value)}>
                <option value="">Choose Category</option>
                {categories.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
              <div className="mb-3">
                <button className="btn btn-secondary me-2" onClick={() => setStep(3)}>Previous</button>
                <button className="btn btn-primary" onClick={() => setStep(5)} disabled={!category}>Next</button>
              </div>
              <InfluencerList
                influencers={categoryFilteredInfluencers}
                platform={platform}
                adType={adType}
                platformIcons={platformIcons}
              />

            </>
          )}

          {/* STEP 5: Ad Type */}
          {step === 5 && (
            <>
              <h5>Select Ad Type</h5>
              <select className="form-select mb-3" value={adType} onChange={e => setAdType(e.target.value)}>
                <option value="">Choose Ad Type</option>
                <option value="cgc">Creator Generated Content</option>
                <option value="post">Media Upload</option>
              </select>
              <div className="mb-3">
                <button className="btn btn-secondary me-2" onClick={() => setStep(4)}>Previous</button>
                <button className="btn btn-primary" onClick={() => setStep(6)} disabled={!adType}>Next</button>
              </div>
              <Summary adType={adType} budget={budget} platform={platform} tier={tier} category={category} />
              <InfluencerList
                influencers={categoryFilteredInfluencers}
                platform={platform}
                adType={adType}
                platformIcons={platformIcons}
              />

            </>
          )}

          {/* STEP 6: Budget */}
          {/* STEP 6: Budget */}
          {step === 6 && (
            <>
              <h5>Define Your Budget</h5>
              <input
                type="number"
                className="form-control mb-3"
                placeholder="Enter budget in KES"
                value={budget}
                onChange={e => setBudget(e.target.value)}
              />

              <div className="mb-3">
                <button className="btn btn-secondary me-2" onClick={() => setStep(5)}>Previous</button>
                <button
                  className="btn btn-primary"
                  disabled={!budget}
                  onClick={() => {
                    const selectedInfluencerIds = budgetFiltered.map(u => u.id);
                    const adData = { platform, tier, category, adType, budget, influencerIds: selectedInfluencerIds };
                    navigate('/user/submit-ads', { state: adData });
                  }}
                >
                  Get Started
                </button>
              </div>

              <Summary
                adType={adType}
                budget={budget}
                platform={platform}
                tier={tier}
                category={category}
                budgetFiltered={budgetFiltered}
              />

              <InfluencerList
                influencers={budgetFiltered}
                platform={platform}
                adType={adType}
                platformIcons={platformIcons}
              />
            </>
          )}


        </section>
      </div>

      <div className="mobileOnly">
        <BottomNavUser />
      </div>
    </div>
  )
}

const Summary = ({ adType, budget, platform, tier, category, budgetFiltered }) => (
  <>
    <div className="alert alert-info mb-3">
      <strong>Selected:</strong> {platform ? `Platform: ${platform}` : ''} {tier ? `| Tier: ${tier}` : ''} {category ? `| Category: ${category}` : ''} {adType ? `| Ad Type: ${adType === 'cgc' ? 'Creator Generated Content' : 'Media Upload'}` : ''} {budget ? `| Budget: KES ${budget}` : ''}
    </div>
    {budget && budgetFiltered && (
      <div className="alert alert-success mt-2">
        {budgetFiltered.length} influencer{budgetFiltered.length !== 1 ? 's' : ''} selected within your budget
      </div>
    )}
  </>
)



const InfluencerList = ({ influencers, platform, adType, platformIcons }) => {
  // Only filter by price if adType is selected
  const filteredInfluencers = adType
    ? influencers.filter(user => {
      if (!platform || !user.servicePricing?.[platform]) return false

      const platPricing = user.servicePricing[platform]
      let price = 0
      if (adType === 'cgc') price = platPricing.creatorGeneratedContent?.price || 0
      else if (adType === 'post') price = platPricing.directBrandContent?.post || 0

      return price > 0
    })
    : influencers

  return (
    <div className="mt-3 influencerGrid">
      {filteredInfluencers.map(user => {
        const influencerLink = `/${user.theUsername}`  // move outside JSX
        let socialsToDisplay = []

        if (platform && (user.socials?.[platform] === true || user.socials?.[platform] === 'selected')) {
          socialsToDisplay.push({
            platform,
            count:
              platform === 'youtube'
                ? user.socialStats?.youtube?.subscribers || 0
                : user.socialStats?.[platform]?.followers || 0,
            label: platform === 'youtube' ? 'subs' : 'followers'
          })
        }

        // Get price for display
        let price = null
        if (adType && user.servicePricing?.[platform]) {
          const platPricing = user.servicePricing[platform]
          price =
            adType === 'cgc'
              ? platPricing.creatorGeneratedContent?.price
              : platPricing.directBrandContent?.post
        }

        return (
          <Link key={user.id} to={influencerLink} className="shadow-sm influencerCard">
            <div className="h-100">
              <h6>{user.userName}</h6>
              <p>{user.countryName}</p>
              <div className="profileImgWrap mb-2">
                <img src={user.profileImage || defProfile} alt="profile" className="rounded-circle" />
              </div>
              <div className="d-flex flex-wrap gap-2 BottinfluencerCard">
                {socialsToDisplay.map(s => (
                  <div key={s.platform} className="d-flex align-items-center gap-1">
                    <FontAwesomeIcon icon={platformIcons[s.platform]} />
                    <span>{s.count.toLocaleString()} {s.label}</span>
                  </div>
                ))}
                {price && price > 0 && (
                  <div className="d-flex align-items-center gap-1 ">
                    <span>Price: KES {price.toLocaleString()}</span>
                  </div>
                )}
              </div>
            </div>
          </Link>
        )
      })}
    </div>
  )
}





export default RunAds